import React, { useState, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { 
  Zap,
  Upload, 
  FileSpreadsheet, 
  Loader2, 
  CheckCircle, 
  AlertCircle,
  ArrowRight,
  Search,
  ShoppingCart,
  TrendingUp,
  Check,
  X,
  ChevronDown
} from 'lucide-react';
import { toast } from 'sonner';
import { ticketService } from '../../services/api';
import { 
  normalizeRGCourt, 
  normalizeRGCategory, 
  normalizeRGSession,
  normalizeRGEventName,
  parseRGDate, 
  parseRGPrice 
} from '../../data/rolandGarros';

import { autoFillMatchName, findMatch, buildMatchName, normalizeSession, normalizeCourt, normalizePhase, parsePlacement } from '../../data/rgSchedule';

// World Cup imports
import { 
  normalizeMatchNumber as normalizeWCMatchNumber,
  getMatchByNumber as getWCMatchByNumber,
  getMatchDisplayName as getWCMatchDisplayName,
  normalizeVenue as normalizeWCVenue,
  normalizeStage as normalizeWCStage
} from '../../data/worldCupSchedule';
import {
  normalizeRugbyMatchNumber,
  getRugbyMatchByNumber,
  getRugbyMatchDisplayName,
  detectRugbyMatchFromText
} from '../../data/rugbySchedule';

// Available YAS fields for mapping - COMMON FIELDS ACHAT (tous les projets)
const COMMON_FIELDS_ACHAT = [
  { key: 'ignore', label: '❌ Ignorer', icon: X, color: 'text-red-500' },
  { key: 'evenement', label: '🎫 Événement/Match', icon: null },
  { key: 'match_no', label: '🔢 N° Match (auto-convert)', icon: null },
  { key: 'date_evenement', label: '📅 Date événement', icon: null },
  { key: 'heure_evenement', label: '🕐 Heure', icon: null },
  { key: 'date_operation', label: '📆 Date d\'achat', icon: null },
  { key: 'lieu', label: '🏟️ Lieu/Stade', icon: null },
  { key: 'ville', label: '🏙️ Ville', icon: null },
  { key: 'pays', label: '🌍 Pays', icon: null },
  { key: 'categorie', label: '🏷️ Catégorie', icon: null },
  { key: 'qty', label: '🔢 Quantité', icon: null },
  { key: 'rang', label: '💺 Rang/Row', icon: null },
  { key: 'place', label: '🎫 Seat/Place', icon: null },
  { key: 'block', label: '🧱 Bloc/Section', icon: null },
  { key: 'prix', label: '💰 Prix d\'achat', icon: null },
  { key: 'total', label: '💵 Total', icon: null },
  { key: 'source', label: '📱 Source/Plateforme', icon: null },
  { key: 'ref_achat', label: '🔖 Réf d\'Achat', icon: null },
  { key: 'email_compte', label: '📬 Email Compte', icon: null },
  { key: 'pw_compte', label: '🔐 Mot de passe Compte', icon: null },
  { key: 'nom_client', label: '👤 Nom Client', icon: null },
  { key: 'mail_client', label: '📧 Email Client', icon: null },
  { key: 'dob_client', label: '🎂 Date de naissance Client', icon: null },
  { key: 'acheteur_original', label: '🏷️ Acheteur Original', icon: null },
  { key: 'note', label: '📝 Note', icon: null },
];

// COMMON FIELDS VENTE (tous les projets)
const COMMON_FIELDS_VENTE = [
  { key: 'ignore', label: '❌ Ignorer', icon: X, color: 'text-red-500' },
  { key: 'evenement', label: '🎫 Événement/Match', icon: null },
  { key: 'match_no', label: '🔢 N° Match (auto-convert)', icon: null },
  { key: 'date_evenement', label: '📅 Date événement', icon: null },
  { key: 'heure_evenement', label: '🕐 Heure', icon: null },
  { key: 'date_operation', label: '📆 Date de vente', icon: null },
  { key: 'lieu', label: '🏟️ Lieu/Stade', icon: null },
  { key: 'ville', label: '🏙️ Ville', icon: null },
  { key: 'categorie', label: '🏷️ Catégorie', icon: null },
  { key: 'qty', label: '🔢 Quantité', icon: null },
  { key: 'rang', label: '💺 Rang/Row', icon: null },
  { key: 'place', label: '🎫 Seat/Place', icon: null },
  { key: 'block', label: '🧱 Bloc/Section', icon: null },
  { key: 'prix', label: '💰 Prix de vente', icon: null },
  { key: 'total', label: '💵 Total', icon: null },
  { key: 'source', label: '📱 Plateforme de vente', icon: null },
  { key: 'ref_vente', label: '🔖 Réf de Vente', icon: null },
  { key: 'nom_client', label: '👤 Nom Client', icon: null },
  { key: 'mail_client', label: '📧 Email Client', icon: null },
  { key: 'dob_client', label: '🎂 Date de naissance Client', icon: null },
  { key: 'mode_livraison', label: '📦 Mode Livraison', icon: null },
  { key: 'preuve_envoi', label: '✅ Preuve Envoi', icon: null },
  { key: 'note', label: '📝 Note', icon: null },
  { key: 'acheteur_original', label: '🏷️ Acheteur Original', icon: null },
];

// ROLAND GARROS specific fields
const ROLAND_GARROS_FIELDS = [
  { key: 'session', label: '🌅 Session (Day/Night)', icon: null },
  { key: 'tribune', label: '🏛️ Tribune', icon: null },
  { key: 'acces', label: '🚪 Accès', icon: null },
  { key: 'escalier', label: '🪜 Escalier', icon: null },
  { key: 'placement', label: '📍 Placement combiné (C9 S51 R11 S122)', icon: null },
];

// WORLD CUP specific fields
const WORLD_CUP_FIELDS = [
  { key: 'phase', label: '🏆 Phase (Group/R16/QF/SF/Final)', icon: null },
  { key: 'section', label: '🎫 Section', icon: null },
  { key: 'gate', label: '🚪 Gate/Porte', icon: null },
];

// Get fields based on project AND import type (ACHAT/VENTE)
const getFieldsForProject = (projectName, importType = 'ACHAT') => {
  const project = projectName?.toUpperCase() || '';
  const baseFields = importType === 'VENTE' ? COMMON_FIELDS_VENTE : COMMON_FIELDS_ACHAT;
  
  if (project.includes('ROLAN') || project.includes('RG')) {
    return [...baseFields, ...ROLAND_GARROS_FIELDS];
  } else if (project.startsWith('WORLD CUP') || project.includes('COUPE DU MONDE')) {
    return [...baseFields, ...WORLD_CUP_FIELDS];
  }
  
  // Default: base fields + all project-specific
  return [...baseFields, ...ROLAND_GARROS_FIELDS, ...WORLD_CUP_FIELDS];
};

// ============ PREVIEW COLUMNS BY PROJECT AND TYPE ============

// ROLAND GARROS - ACHAT
const PREVIEW_COLUMNS_RG_ACHAT = [
  { key: 'ref_achat', label: 'Réf Achat' },
  { key: 'date_operation', label: 'Date Achat' },
  { key: 'source', label: 'Source' },
  { key: 'evenement', label: 'Événement' },
  { key: 'date_evenement', label: 'Date Evt' },
  { key: 'session', label: 'Session' },
  { key: 'lieu', label: 'Court' },
  { key: 'tribune', label: 'Tribune' },
  { key: 'acces', label: 'Accès' },
  { key: 'escalier', label: 'Escalier' },
  { key: 'rang', label: 'Rang' },
  { key: 'place', label: 'Seat' },
  { key: 'categorie', label: 'Cat.' },
  { key: 'prix', label: 'Prix' },
  { key: 'email_compte', label: 'Email Compte' },
  { key: 'pw_compte', label: 'PW' },
];

// ROLAND GARROS - VENTE
const PREVIEW_COLUMNS_RG_VENTE = [
  { key: 'ref_vente', label: 'Réf Vente' },
  { key: 'date_operation', label: 'Date Vente' },
  { key: 'source', label: 'Plateforme' },
  { key: 'evenement', label: 'Événement' },
  { key: 'date_evenement', label: 'Date Evt' },
  { key: 'session', label: 'Session' },
  { key: 'lieu', label: 'Court' },
  { key: 'categorie', label: 'Cat.' },
  { key: 'rang', label: 'Rang' },
  { key: 'place', label: 'Seat' },
  { key: 'prix', label: 'Prix' },
  { key: 'nom_client', label: 'Client' },
  { key: 'mail_client', label: 'Email Client' },
  { key: 'mode_livraison', label: 'Livraison' },
];

// WORLD CUP - ACHAT
const PREVIEW_COLUMNS_WC_ACHAT = [
  { key: 'ref_achat', label: 'Réf Achat' },
  { key: 'date_operation', label: 'Date Achat' },
  { key: 'source', label: 'Source' },
  { key: 'evenement', label: 'Match' },
  { key: 'date_evenement', label: 'Date Match' },
  { key: 'lieu', label: 'Stade' },
  { key: 'ville', label: 'Ville' },
  { key: 'phase', label: 'Phase' },
  { key: 'section', label: 'Section' },
  { key: 'rang', label: 'Row' },
  { key: 'place', label: 'Seat' },
  { key: 'categorie', label: 'Cat.' },
  { key: 'prix', label: 'Prix' },
  { key: 'email_compte', label: 'Email Compte' },
  { key: 'pw_compte', label: 'PW' },
];

// WORLD CUP - VENTE
const PREVIEW_COLUMNS_WC_VENTE = [
  { key: 'ref_vente', label: 'Réf Vente' },
  { key: 'date_operation', label: 'Date Vente' },
  { key: 'source', label: 'Plateforme' },
  { key: 'evenement', label: 'Match' },
  { key: 'date_evenement', label: 'Date Match' },
  { key: 'lieu', label: 'Stade' },
  { key: 'ville', label: 'Ville' },
  { key: 'phase', label: 'Phase' },
  { key: 'categorie', label: 'Cat.' },
  { key: 'rang', label: 'Row' },
  { key: 'place', label: 'Seat' },
  { key: 'prix', label: 'Prix' },
  { key: 'nom_client', label: 'Client' },
  { key: 'mail_client', label: 'Email Client' },
  { key: 'mode_livraison', label: 'Livraison' },
];

// DEFAULT - ACHAT
const PREVIEW_COLUMNS_DEFAULT_ACHAT = [
  { key: 'ref_achat', label: 'Réf' },
  { key: 'date_operation', label: 'Date Achat' },
  { key: 'source', label: 'Source' },
  { key: 'evenement', label: 'Événement' },
  { key: 'date_evenement', label: 'Date' },
  { key: 'lieu', label: 'Lieu' },
  { key: 'categorie', label: 'Cat.' },
  { key: 'rang', label: 'Rang' },
  { key: 'place', label: 'Seat' },
  { key: 'prix', label: 'Prix' },
  { key: 'email_compte', label: 'Email' },
];

// DEFAULT - VENTE
const PREVIEW_COLUMNS_DEFAULT_VENTE = [
  { key: 'ref_vente', label: 'Réf' },
  { key: 'date_operation', label: 'Date Vente' },
  { key: 'source', label: 'Plateforme' },
  { key: 'evenement', label: 'Événement' },
  { key: 'date_evenement', label: 'Date' },
  { key: 'lieu', label: 'Lieu' },
  { key: 'categorie', label: 'Cat.' },
  { key: 'prix', label: 'Prix' },
  { key: 'nom_client', label: 'Client' },
  { key: 'mail_client', label: 'Email' },
];

// Get preview columns based on project AND import type
const getPreviewColumnsForProject = (projectName, importType = 'ACHAT') => {
  const project = projectName?.toUpperCase() || '';
  const isVente = importType === 'VENTE';
  
  if (project.includes('ROLAN') || project.includes('RG')) {
    return isVente ? PREVIEW_COLUMNS_RG_VENTE : PREVIEW_COLUMNS_RG_ACHAT;
  } else if (project.startsWith('WORLD CUP') || project.includes('COUPE DU MONDE')) {
    return isVente ? PREVIEW_COLUMNS_WC_VENTE : PREVIEW_COLUMNS_WC_ACHAT;
  }
  
  return isVente ? PREVIEW_COLUMNS_DEFAULT_VENTE : PREVIEW_COLUMNS_DEFAULT_ACHAT;
};

// Legacy: Keep YAS_FIELDS for backward compatibility
const YAS_FIELDS = [...COMMON_FIELDS_ACHAT, ...ROLAND_GARROS_FIELDS, ...WORLD_CUP_FIELDS];

// Auto-detect what a column might be based on its values
const detectColumnType = (values, headerName = '') => {
  const nonEmpty = values.filter(v => v && v.toString().trim());
  if (nonEmpty.length === 0) return 'ignore';
  
  const sample = nonEmpty[0].toString().trim();
  const header = headerName.toLowerCase();
  
  // ========== HEADER NAME DETECTION ==========
  
  // REF / Référence
  if (header === 'ref' || header === 'reference' || header === 'réf' || header === 'référence') return 'ref_achat';
  if (header.includes('ref') && (header.includes('vente') || header.includes('sale'))) return 'ref_vente';
  if (header.includes('ref') && header.includes('achat')) return 'ref_achat';
  
  // Événement / Match / Round / Phase
  if (header === 'evenement' || header === 'événement' || header === 'event' || header === 'match') return 'evenement';
  if (header.includes('match') || header.includes('event') || header.includes('événement') || header.includes('evenement')) return 'evenement';
  if (header === 'round' || header === 'tour' || header === 'phase') return 'evenement';
  
  // Court / Lieu / Stade / Cours (pour Roland Garros notamment)
  if (header === 'cours' || header === 'court' || header === 'lieu' || header === 'stade' || header === 'stadium') return 'lieu';
  if (header.includes('cours') || header.includes('court') || header.includes('lieu') || header.includes('stade') || header.includes('stadium')) return 'lieu';
  
  // Session (Day/Evening)
  if (header === 'session' || header.includes('session')) return 'session';
  
  // Tribune / Stand
  if (header === 'tribune' || header === 'stand' || header.includes('tribune') || header.includes('stand')) return 'tribune';
  
  // Accès / Porte / Gate
  if (header === 'acces' || header === 'accès' || header === 'porte' || header === 'gate') return 'acces';
  if (header.includes('acces') || header.includes('accès') || header.includes('porte') || header.includes('gate')) return 'acces';
  
  // Escalier
  if (header === 'escalier' || header.includes('escalier') || header === 'stairs' || header === 'esc') return 'escalier';
  
  // Seat / Siège / Place
  if (header === 'seat' || header === 'siege' || header === 'siège' || header === 'place') return 'place';
  if (header.includes('seat') || header.includes('siege') || header.includes('siège')) return 'place';
  
  // Note / Commentaire
  if (header === 'note' || header === 'notes' || header === 'commentaire' || header === 'comment') return 'note';
  if (header.includes('note') || header.includes('commentaire')) return 'note';
  
  // Livraison / Delivery
  if (header.includes('livraison') || header.includes('delivery') || header.includes('expédition')) return 'mode_livraison';
  
  // Date événement
  if (header === 'date evenement' || header === 'date événement' || header === 'date event') return 'date_evenement';
  if (header.includes('date') && (header.includes('evenement') || header.includes('événement') || header.includes('event'))) return 'date_evenement';
  
  // Date de vente/opération
  if (header === 'date vente' || header === 'date achat' || header === 'date operation') return 'date_operation';
  if (header.includes('date') && (header.includes('vente') || header.includes('achat') || header.includes('operation') || header.includes('sale'))) return 'date_operation';
  
  // Match number
  if (header.includes('match_no') || header.includes('match n') || header === 'n° match') return 'match_no';
  
  // Catégorie
  if (header === 'categorie' || header === 'catégorie' || header === 'category' || header === 'cat') return 'categorie';
  if (header.includes('categ') || header.includes('cat')) return 'categorie';
  
  // Quantité
  if (header === 'qty' || header === 'quantite' || header === 'quantité' || header === 'quantity') return 'qty';
  if (header.includes('qty') || header.includes('quantit')) return 'qty';
  
  // Prix
  if (header === 'prix' || header === 'price' || header === 'tarif') return 'prix';
  if (header.includes('prix') || header.includes('price') || header.includes('tarif')) return 'prix';
  
  // Source / Plateforme
  if (header === 'source' || header === 'plateforme' || header === 'platform') return 'source';
  if (header.includes('source') || header.includes('plateforme')) return 'source';
  
  // Email
  if (header.includes('e-mail') || header.includes('email') || header.includes('mail')) {
    if (header.includes('client')) return 'mail_client';
    if (header.includes('compte')) return 'email_compte';
    return 'email_compte';
  }
  
  // Password
  if (header.includes('pw') || header.includes('password') || header.includes('mot de passe')) return 'pw_compte';
  
  // Nom client
  if (header.includes('nom') && header.includes('client')) return 'nom_client';
  if (header === 'client' || header === 'nom client') return 'nom_client';
  
  // DOB / Date de naissance
  if (header.includes('dob') || header.includes('naissance') || header.includes('birth')) return 'dob_client';
  
  // Acheteur Original
  if (header.includes('acheteur') || header === 'original buyer' || header === 'buyer') return 'acheteur_original';
  
  // Nom client - aussi "name", "names"
  if (header === 'name' || header === 'names' || header === 'nom') return 'nom_client';
  
  // Session - aussi "slot"
  if (header === 'slot') return 'session';
  
  // Qty - aussi "# tickets", "tickets"
  if (header === '# tickets' || header === 'tickets' || header === 'nb tickets' || header === 'nb') return 'qty';
  
  // Ville / Pays
  if (header.includes('ville') || header.includes('city')) return 'ville';
  if (header.includes('pays') || header.includes('country')) return 'pays';
  
  // Date générique
  if (header.includes('date')) return 'date_evenement';
  
  // Rang / Row / Block
  if (header.includes('rang') || header.includes('row')) return 'rang';
  if (header.includes('block') || header.includes('bloc') || header.includes('section')) return 'block';
  
  // ========== CONTENT PATTERN DETECTION ==========
  
  // Référence de vente (format ASSAFIS-XX, CHAROU01-X, ESA01-XX, etc.)
  if (/^[A-Z0-9]+-\d+$/i.test(sample) || /^[A-Z]+\d+-\d+$/i.test(sample)) return 'ref_vente';
  if (/^\d{9,12}$/.test(sample)) return 'ref_vente';
  if (/^ONE\d+[A-Z]+$/i.test(sample)) return 'ref_vente'; // Format ONE38BXN26W
  
  // Événement Roland Garros
  if (/FINAL|SEMI|QUARTER|MAI|JUIN|DAY|NIGHT|\(RG\)/i.test(sample)) return 'evenement';
  
  // Court Roland Garros
  if (/CHATRIER|LENGLEN|MATHIEU|RG\s/i.test(sample)) return 'lieu';
  
  // Catégorie
  if (/^(CAT|CATEGORY|CATEGORIE|CATÉGORIE)\s*[1-9OR]$/i.test(sample)) return 'categorie';
  if (/^GOLD$/i.test(sample)) return 'categorie';
  if (/^[1-4]$/.test(sample)) return 'categorie';
  if (/^cat/i.test(sample)) return 'categorie';
  
  // Quantité
  if (/^[1-9]$/.test(sample)) return 'qty';
  
  // Prix (avec ou sans €)
  if (/[€$£]/.test(sample)) return 'prix';
  if (/^\d{1,6}[,.]?\d*\s*€?$/.test(sample.replace(/\s/g, ''))) return 'prix';
  
  // Match number
  if (/^\d{1,3}$/.test(sample) && parseInt(sample) <= 150) return 'match_no';
  
  // Email
  if (sample.includes('@')) return 'email_compte';
  
  // Date (formats variés)
  if (/^\d{1,2}[\/\-]\d{1,2}[\/\-]\d{2,4}/.test(sample)) return 'date_evenement';
  if (/^\d{4}-\d{2}-\d{2}/.test(sample)) return 'date_evenement';
  if (/^(lun|mar|mer|jeu|ven|sam|dim)\.\s*\d{1,2}\/\d{1,2}\/\d{2}/i.test(sample)) return 'date_evenement';
  
  // World Cup / Match
  if (/ vs /i.test(sample) || /world cup|match|final/i.test(sample)) return 'evenement';
  
  return 'ignore';
};


// Parse CSV/TSV data - handles quoted fields with newlines
const parseData = (text, delimiter = '\t', treatFirstRowAsHeader = null) => {
  if (!text.trim()) return { headers: [], rows: [], columns: [] };
  
  // Normalize line endings (handle Windows \r\n and Mac \r)
  const normalizedText = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').trim();
  
  // Parse the text character by character to handle quoted fields with newlines
  const rows = [];
  let currentRow = [];
  let currentCell = '';
  let inQuotes = false;
  
  for (let i = 0; i < normalizedText.length; i++) {
    const char = normalizedText[i];
    
    if (char === '"') {
      // Check for escaped quote ""
      if (inQuotes && normalizedText[i + 1] === '"') {
        currentCell += '"';
        i++; // Skip the next quote
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === delimiter && !inQuotes) {
      currentRow.push(currentCell.trim());
      currentCell = '';
    } else if (char === '\n' && !inQuotes) {
      currentRow.push(currentCell.trim());
      if (currentRow.some(cell => cell.length > 0)) {
        rows.push(currentRow);
      }
      currentRow = [];
      currentCell = '';
    } else {
      // For newlines inside quotes, replace with space to keep it clean
      if (char === '\n' && inQuotes) {
        currentCell += ' ';
      } else {
        currentCell += char;
      }
    }
  }
  
  // Don't forget the last cell and row
  currentRow.push(currentCell.trim());
  if (currentRow.some(cell => cell.length > 0)) {
    rows.push(currentRow);
  }
  
  if (rows.length === 0) return { headers: [], rows: [], columns: [] };
  
  // Determine if first row is headers
  let hasHeaders;
  if (treatFirstRowAsHeader !== null) {
    // User explicitly chose via checkbox
    hasHeaders = treatFirstRowAsHeader;
  } else {
    // Auto-detect: Check if first row looks like headers
    const firstRow = rows[0];
    const headerLikeCells = firstRow.filter(cell => {
      if (!cell || cell.length === 0) return false;
      if (/^\d+$/.test(cell)) return false;
      if (/^\d{4}-\d{2}-\d{2}/.test(cell)) return false;
      if (/^\d{2}\/\d{2}\/\d{4}/.test(cell)) return false; // DD/MM/YYYY dates
      if (/^\d{2}\/\d{2}\/\d{2}/.test(cell)) return false; // DD/MM/YY dates
      if (/[€$£]/.test(cell)) return false;
      if (/@/.test(cell)) return false;
      if (/^\d+[,.]?\d*$/.test(cell.replace(/\s/g, ''))) return false;
      return /^[a-zA-ZÀ-ÿ_\-\/\s()]+$/.test(cell) && cell.length >= 2 && cell.length < 40;
    });
    const nonEmptyCells = firstRow.filter(c => c && c.length > 0);
    // More conservative: require 60%+ header-like cells to treat as headers
    hasHeaders = nonEmptyCells.length >= 3 && headerLikeCells.length > nonEmptyCells.length * 0.6;
  }
  
  const headers = hasHeaders ? rows[0] : rows[0].map((_, i) => `Colonne ${i + 1}`);
  const dataRows = hasHeaders ? rows.slice(1) : rows;
  
  // Build column info with samples and auto-detection
  const columns = headers.map((header, idx) => {
    const values = dataRows.map(row => row[idx] || '').slice(0, 10);
    const detectedType = detectColumnType(values, header);
    return {
      index: idx,
      header: header,
      sample: values[0] || '',
      samples: values.slice(0, 3),
      detectedType,
      mappedTo: detectedType
    };
  });
  
  return { headers, rows: dataRows, columns };
};

// Match names cache + full details from DB
let matchNamesCache = {};
let matchDetailsCache = {};

// ========== MAPPINGS PRÉ-CONFIGURÉS PAR PROJET ==========
// Ces mappings sont automatiquement proposés selon le projet actif

const PROJECT_DEFAULT_MAPPINGS = {
  'ROLAND GARROS': {
    name: 'Roland Garros Standard',
    description: 'Format standard pour les imports Roland Garros',
    // Mapping basé sur les headers courants
    headerPatterns: {
      'ref': 'ref_vente',  // Will be overridden to ref_achat for ACHAT imports
      'reference': 'ref_vente',
      'date vente': 'date_operation',
      'date achat': 'date_operation',
      'date': 'date_evenement',
      'source': 'source',
      'evenement': 'evenement',
      'événement': 'evenement',
      'round': 'evenement',
      'tour': 'evenement',
      'phase': 'evenement',
      'jour/nuit': 'session',
      'day/night': 'session',
      'session': 'session',
      'cours': 'lieu',
      'court': 'lieu',
      'lieu': 'lieu',
      'date evenement': 'date_evenement',
      'date événement': 'date_evenement',
      'categorie': 'categorie',
      'catégorie': 'categorie',
      'category': 'categorie',
      'cat': 'categorie',
      'tribune': 'tribune',
      'stand': 'tribune',
      'acces': 'acces',
      'accès': 'acces',
      'porte': 'acces',
      'escalier': 'escalier',
      'esc': 'escalier',
      'row': 'rang',
      'rang': 'rang',
      'seat': 'place',
      'place': 'place',
      'siege': 'place',
      'siège': 'place',
      'qty': 'qty',
      'quantite': 'qty',
      'quantité': 'qty',
      'prix': 'prix',
      'price': 'prix',
      'tarif': 'prix',
      'note': 'note',
      'notes': 'note',
      'commentaire': 'note',
      'mail du compte': 'email_compte',
      'email compte': 'email_compte',
      'email': 'email_compte',
      'e-mail': 'email_compte',
      'pw du compte': 'pw_compte',
      'pw': 'pw_compte',
      'password': 'pw_compte',
      'livraison': 'mode_livraison',
      'client': 'nom_client',
      'nom client': 'nom_client',
      'mail client': 'mail_client',
      'email client': 'mail_client',
      'slot': 'session',
      '# tickets': 'qty',
      'tickets': 'qty',
      'nb tickets': 'qty',
      'stairs': 'escalier',
      'access': 'acces',
      'seats': 'place',
      'name': 'nom_client',
      'names': 'nom_client',
      'dob': 'dob_client',
      'date de naissance': 'dob_client',
      'dob client': 'dob_client',
      'acheteur original': 'acheteur_original',
      'acheteur': 'acheteur_original',
      'unit price': 'prix',
      'manager': 'ignore',
    },
    // Valeurs par défaut pour Roland Garros
    defaults: {
      ville: 'Paris',
      pays: 'France',
    },
    // Flag pour activer la normalisation Roland Garros
    normalize: true,
  },
  'WORLD CUP 2026': {
    name: 'World Cup 2026 Standard',
    description: 'Format standard pour les tickets World Cup',
    headerPatterns: {
      'match_no': 'match_no',
      'match': 'evenement',
      'event': 'evenement',
      'category': 'categorie',
      'qty': 'qty',
      'e-mail': 'email_compte',
      'email': 'email_compte',
      'pw': 'pw_compte',
      'source': 'source',
      'prix': 'prix',
      'ref': 'ref_vente',
    },
    defaults: {}
  }
};

// Fonction pour appliquer le mapping par défaut d'un projet
const applyProjectDefaultMapping = (headers, projectName, importType = 'VENTE') => {
  const projectConfig = PROJECT_DEFAULT_MAPPINGS[projectName?.toUpperCase()];
  if (!projectConfig) return null;
  
  const mappings = {};
  headers.forEach((header, idx) => {
    const headerLower = header.toLowerCase().trim();
    
    // Chercher une correspondance dans les patterns du projet
    for (const [pattern, field] of Object.entries(projectConfig.headerPatterns)) {
      if (headerLower === pattern || headerLower.includes(pattern)) {
        // Adapter ref_vente → ref_achat pour les imports ACHAT
        if (field === 'ref_vente' && importType === 'ACHAT') {
          mappings[idx] = 'ref_achat';
        } else {
          mappings[idx] = field;
        }
        break;
      }
    }
    
    // Si pas de correspondance, mettre ignore
    if (!mappings[idx]) {
      mappings[idx] = 'ignore';
    }
  });
  
  return { mappings, defaults: projectConfig.defaults, name: projectConfig.name };
};

// API URL for saved mappings
const API_URL = '';

// Get saved mappings from database
const getSavedMappingsFromDB = async (project = '') => {
  try {
    const url = project 
      ? `${API_URL}/api/import-mappings?project=${encodeURIComponent(project)}`
      : `${API_URL}/api/import-mappings`;
    const response = await fetch(url);
    if (response.ok) {
      return await response.json();
    }
    return [];
  } catch (error) {
    console.error('Error fetching mappings:', error);
    return [];
  }
};

// Save mapping to database
const saveMappingToDB = async (name, headers, mappings, project = '') => {
  try {
    const response = await fetch(`${API_URL}/api/import-mappings`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name,
        headers: headers.join('|'),
        mappings,
        project
      })
    });
    if (response.ok) {
      const data = await response.json();
      return data.mapping;
    }
    return null;
  } catch (error) {
    console.error('Error saving mapping:', error);
    return null;
  }
};

// Delete mapping from database
const deleteMappingFromDB = async (mappingId) => {
  try {
    const response = await fetch(`${API_URL}/api/import-mappings/${mappingId}`, {
      method: 'DELETE'
    });
    return response.ok;
  } catch (error) {
    console.error('Error deleting mapping:', error);
    return false;
  }
};

// Local storage key for saved mappings (kept as fallback)
const SAVED_MAPPINGS_KEY = 'yas_import_mappings';

// Get saved mappings from localStorage (fallback)
const getSavedMappings = () => {
  try {
    const saved = localStorage.getItem(SAVED_MAPPINGS_KEY);
    return saved ? JSON.parse(saved) : [];
  } catch {
    return [];
  }
};

// Save mapping to localStorage (fallback)
const saveMappingToStorage = (name, headers, mappings) => {
  try {
    const saved = getSavedMappings();
    const newMapping = {
      id: Date.now(),
      name,
      headers: headers.join('|'), // Store headers as string for comparison
      mappings,
      createdAt: new Date().toISOString()
    };
    // Keep only last 10 mappings
    const updated = [newMapping, ...saved].slice(0, 10);
    localStorage.setItem(SAVED_MAPPINGS_KEY, JSON.stringify(updated));
    return true;
  } catch {
    return false;
  }
};

// Find matching saved mapping based on headers
const findMatchingSavedMapping = (headers) => {
  const saved = getSavedMappings();
  const headersStr = headers.join('|');
  
  // Exact header match first
  const exact = saved.find(m => m.headers === headersStr);
  if (exact) return exact;
  
  // Fallback: match by column count for data without real headers (Colonne 1, Colonne 2...)
  const isGeneric = headers.length > 0 && headers[0]?.startsWith('Colonne ');
  if (isGeneric) {
    return saved.find(m => {
      const savedHeaders = m.headers.split('|');
      const savedIsGeneric = savedHeaders[0]?.startsWith('Colonne ');
      return savedIsGeneric && savedHeaders.length === headers.length;
    });
  }
  
  return null;
};

// Delete saved mapping
const deleteSavedMapping = (id) => {
  try {
    const saved = getSavedMappings();
    const updated = saved.filter(m => m.id !== id);
    localStorage.setItem(SAVED_MAPPINGS_KEY, JSON.stringify(updated));
    return true;
  } catch {
    return false;
  }
};

export const SmartImportModal = ({ open, onOpenChange, onImport, projectName = '' }) => {
  // Steps: 1=paste/upload, 2=mapping, 3=import
  const [step, setStep] = useState(1);
  const [rawData, setRawData] = useState('');
  const [parsedData, setParsedData] = useState({ headers: [], rows: [], columns: [] });
  const [columnMappings, setColumnMappings] = useState({});
  const [importType, setImportType] = useState('ACHAT');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [searchField, setSearchField] = useState('');
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [savedMappingName, setSavedMappingName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [matchedSavedMapping, setMatchedSavedMapping] = useState(null);
  const [showSavedMappings, setShowSavedMappings] = useState(false);
  const [showDataPreview, setShowDataPreview] = useState(false);
  const [projectDefaultApplied, setProjectDefaultApplied] = useState(false);
  const [hasHeader, setHasHeader] = useState(true); // Checkbox: première ligne = en-têtes
  const [dbMappings, setDbMappings] = useState([]); // Mappings from database
  const [loadingMappings, setLoadingMappings] = useState(false);
  
  // Duplicate check state
  const [duplicateCheckResult, setDuplicateCheckResult] = useState(null);
  const [checkingDuplicates, setCheckingDuplicates] = useState(false);

  // Load mappings from database on mount
  useEffect(() => {
    const loadMappings = async () => {
      setLoadingMappings(true);
      const mappings = await getSavedMappingsFromDB(projectName);
      setDbMappings(mappings);
      setLoadingMappings(false);
    };
    if (open) {
      loadMappings();
    }
  }, [open, projectName]);

  // Function to refresh mappings from DB
  const refreshMappings = async () => {
    const mappings = await getSavedMappingsFromDB(projectName);
    setDbMappings(mappings);
  };

  // Find matching saved mapping based on headers (uses dbMappings)
  const findMatchingMapping = (headers) => {
    const headersStr = headers.join('|');
    
    // Exact header match first
    const exact = dbMappings.find(m => m.headers === headersStr);
    if (exact) return exact;
    
    // Fallback: match by column count for data without real headers (Colonne 1, Colonne 2...)
    const isGeneric = headers.length > 0 && headers[0]?.startsWith('Colonne ');
    if (isGeneric) {
      return dbMappings.find(m => {
        const savedHeaders = m.headers.split('|');
        const savedIsGeneric = savedHeaders[0]?.startsWith('Colonne ');
        return savedIsGeneric && savedHeaders.length === headers.length;
      });
    }
    
    return null;
  };

  // Auto-detect column mappings based on headers
  const autoDetectMappings = useCallback((headers) => {
    const fieldMappings = {
      "date_evenement": ["DATE", "DATE EVENT", "DATE EVENEMENT", "DATE EVT", "EVENT DATE", "JOUR"],
      "evenement": ["GAME", "ROUND", "MATCH", "EVENEMENT", "EVENT", "EVENT NAME", "TOUR", "JOURNEE"],
      "session": ["SESSION", "DAY/NIGHT", "JOUR/NUIT", "SEANCE", "DAY", "NIGHT", "SLOT"],
      "lieu": ["COURT", "COURS", "TERRAIN", "STADIUM", "STADE", "LENGLEN", "CHATRIER"],
      "tribune": ["TRIBUNE", "STAND", "SECTION"],
      "acces": ["ACCES", "ACCESS", "PORTE", "GATE", "ENTREE", "ACCÈS"],
      "escalier": ["ESCALIER", "STAIR", "STAIRS", "ESC"],
      "rang": ["ROW", "RANG", "RANGEE", "LIGNE"],
      "place": ["SEAT", "SEATS", "PLACE", "SIEGE", "NUMERO"],
      "placement": ["PLACEMENT"],
      "categorie": ["CATEGORY", "CATEGORIE", "CAT", "CATÉGORIE", "TYPE"],
      "prix": ["PRIX", "PRICE", "TARIF", "COUT", "MONTANT", "UNIT PRICE"],
      "qty": ["QTY", "QUANTITY", "QUANTITE", "NB", "NOMBRE", "# TICKETS", "TICKETS", "# TICKET", "NB TICKETS"],
      "source": ["SOURCE", "PLATEFORME", "PLATFORM", "ORIGINE", "FOURNISSEUR"],
      "ref_achat": ["REF ACHAT", "REF_ACHAT", "REFERENCE ACHAT", "ID ACHAT", "REF"],
      "ref_vente": ["REF VENTE", "REF_VENTE", "REFERENCE VENTE", "ID VENTE"],
      "email_compte": ["MAIL DU COMPTE", "EMAIL COMPTE", "ACCOUNT EMAIL", "E-MAIL COMPTE", "MAIL COMPTE", "MAIL", "EMAIL"],
      "pw_compte": ["PW DU COMPTE", "PASSWORD", "MOT DE PASSE", "PW COMPTE", "MDP", "PASS", "PSW", "PW"],
      "nom_client": ["NOM CLIENT", "CLIENT NAME", "CUSTOMER", "NOM", "NAME", "CLIENT", "NAMES"],
      "mail_client": ["MAIL CLIENT", "EMAIL CLIENT", "CLIENT EMAIL", "CUSTOMER EMAIL"],
      "dob_client": ["DOB", "DATE DE NAISSANCE", "DATE NAISSANCE", "BIRTH DATE", "BIRTHDAY", "DOB CLIENT"],
      "acheteur_original": ["ACHETEUR ORIGINAL", "ACHETEUR", "ORIGINAL BUYER", "BUYER"],
      "date_operation": ["DATE VENTE", "DATE ACHAT", "VENTE DATE", "PURCHASE DATE", "VENDU LE", "ACHETE LE"],
      "note": ["NOTE", "NOTES", "COMMENTAIRE", "COMMENT", "REMARQUE"],
      "block": ["BLOCK", "BLOC", "SECTION"],
    };

    const newMappings = {};
    
    headers.forEach((header, idx) => {
      const headerUpper = header.toUpperCase().trim();
      let detected = 'ignore';
      
      // Check for exact matches FIRST, then partial
      for (const [field, aliases] of Object.entries(fieldMappings)) {
        for (const alias of aliases) {
          if (headerUpper === alias) {
            detected = field;
            break;
          }
        }
        if (detected !== 'ignore') break;
      }
      // If no exact match, try partial (skip empty headers)
      if (detected === 'ignore' && headerUpper.length > 0) {
        for (const [field, aliases] of Object.entries(fieldMappings)) {
          for (const alias of aliases) {
            if (headerUpper.includes(alias) || alias.includes(headerUpper)) {
              detected = field;
              break;
            }
          }
          if (detected !== 'ignore') break;
        }
      }
      
      // Special case: if header contains "REF" alone, it's likely ref_achat for ACHAT imports
      if (detected === 'ignore' && headerUpper === 'REF') {
        detected = importType === 'ACHAT' ? 'ref_achat' : 'ref_vente';
      }
      
      newMappings[idx] = detected;
    });
    
    return newMappings;
  }, [importType]);

  // Apply auto-detection when moving to step 2
  const handleGoToStep2 = useCallback(() => {
    if (parsedData.headers.length === 0) return;
    
    if (hasHeader) {
      // Auto-detect based on header names
      const detectedMappings = autoDetectMappings(parsedData.headers);
      setColumnMappings(detectedMappings);
      
      const mappedCount = Object.values(detectedMappings).filter(v => v && v !== 'ignore').length;
      toast.success(`${mappedCount}/${parsedData.headers.length} colonnes détectées automatiquement`);
    } else {
      // No headers - set all to 'ignore' for manual mapping
      const manualMappings = {};
      parsedData.headers.forEach((_, idx) => {
        manualMappings[idx] = 'ignore';
      });
      setColumnMappings(manualMappings);
      toast.info('Mapping manuel : sélectionnez chaque colonne vous-même');
    }
    setStep(2);
  }, [parsedData.headers, autoDetectMappings, hasHeader]);

  // Load match names + details from DB
  useEffect(() => {
    const loadMatchNames = async () => {
      try {
        const data = await ticketService.getMatchNames(projectName);
        if (data.match_names) matchNamesCache = data.match_names;
        if (data.match_details) matchDetailsCache = data.match_details;
      } catch (e) {
        console.log('Could not load match names');
      }
    };
    if (open) loadMatchNames();
  }, [open, projectName]);

  // Reset on close
  useEffect(() => {
    if (!open) {
      setStep(1);
      setRawData('');
      setParsedData({ headers: [], rows: [], columns: [] });
      setColumnMappings({});
      setResult(null);
      setMatchedSavedMapping(null);
      setShowSaveDialog(false);
      setSavedMappingName('');
      setShowDataPreview(false);
      setProjectDefaultApplied(false);
      setHasHeader(true);
    }
  }, [open]);

  // Detect delimiter and parse
  const handleDataChange = useCallback((text, forceHasHeader = null) => {
    setRawData(text);
    if (!text.trim()) {
      setParsedData({ headers: [], rows: [], columns: [] });
      setMatchedSavedMapping(null);
      setProjectDefaultApplied(false);
      return;
    }
    
    // Auto-detect delimiter
    const firstLine = text.split('\n')[0];
    let delimiter = '\t';
    if (!firstLine.includes('\t')) {
      delimiter = firstLine.includes(';') ? ';' : ',';
    }
    
    // forceHasHeader = null → AUTO-DETECT (old behavior, works for all projects)
    // forceHasHeader = true/false → user explicitly toggled checkbox
    const parsed = parseData(text, delimiter, forceHasHeader);
    setParsedData(parsed);
    
    // Update checkbox to reflect what was actually detected
    const detectedHasHeaders = parsed.headers.length > 0 && parsed.headers[0] !== 'Colonne 1';
    if (forceHasHeader === null) {
      setHasHeader(detectedHasHeaders);
    }
    
    // Try saved mappings from DB and project defaults
    const savedMapping = findMatchingMapping(parsed.headers);
    if (savedMapping) {
      setMatchedSavedMapping(savedMapping);
      setColumnMappings(savedMapping.mappings);
      setProjectDefaultApplied(false);
      toast.success(`Mapping "${savedMapping.name}" reconnu et appliqué !`);
      return;
    }
    
    const projectDefault = applyProjectDefaultMapping(parsed.headers, projectName, importType);
    if (projectDefault && Object.values(projectDefault.mappings).some(v => v !== 'ignore')) {
      setColumnMappings(projectDefault.mappings);
      setProjectDefaultApplied(true);
      setMatchedSavedMapping(null);
      toast.success(`Mapping "${projectDefault.name}" appliqué pour ${projectName} !`, {
        description: 'Vous pouvez ajuster les colonnes si nécessaire'
      });
      return;
    }
    
    // Fallback: Use auto-detection
    setMatchedSavedMapping(null);
    setProjectDefaultApplied(false);
    const mappings = {};
    parsed.columns.forEach(col => {
      mappings[col.index] = col.detectedType;
    });
    setColumnMappings(mappings);
  }, [projectName]);

  // Update single column mapping
  const updateMapping = (colIndex, fieldKey) => {
    setColumnMappings(prev => ({ ...prev, [colIndex]: fieldKey }));
    setActiveDropdown(null);
  };

  // Convert parsed data to tickets
  const buildTickets = () => {
    const tickets = [];
    
    for (const row of parsedData.rows) {
      if (row.every(cell => !cell)) continue; // Skip empty rows
      
      const ticket = {
        type: importType,
        ref_achat: '',
        ref_vente: '',
        statut: '',
        evenement: '',
        session: '',  // Day Session / Evening Session
        date_evenement: '',
        date_operation: '',
        heure_evenement: '',
        lieu: '',
        tribune: '',  // Tribune/Stand
        acces: '',  // Accès/Porte
        escalier: '',  // Escalier
        ville: '',
        pays: '',
        categorie: '',
        block: '',
        rang: '',  // Rang/Row
        place: '',  // Seat/Siège
        qty: 1,
        prix: 0,
        devise: 'EUR',
        total: 0,
        source: '',
        nom_client: '',
        mail_client: '',
        dob_client: '',
        acheteur_original: '',
        note: '',
        mode_livraison: '',
        email_compte: '',
        pw_compte: '',
        preuve_envoi: '',
      };
      
      parsedData.columns.forEach((col, idx) => {
        const mapping = columnMappings[idx];
        if (!mapping || mapping === 'ignore') return;
        
        const value = row[idx] || '';
        if (!value.trim()) return; // Ne pas écraser avec du vide
        
        // Handle special conversions
        if (mapping === 'match_no') {
          const matchNo = parseInt(value.trim());
          if (matchNo) {
            const fullName = matchNamesCache[matchNo];
            ticket.evenement = fullName || `M${matchNo.toString().padStart(2, '0')}`;
            // Also enrich with DB details (date, lieu, ville, pays)
            const dbMatch = matchDetailsCache[matchNo];
            if (dbMatch) {
              if (!ticket.date_evenement && dbMatch.date_evenement) ticket.date_evenement = dbMatch.date_evenement;
              if (!ticket.lieu && dbMatch.lieu) ticket.lieu = dbMatch.lieu;
              if (!ticket.ville && dbMatch.ville) ticket.ville = dbMatch.ville;
              if (!ticket.pays && dbMatch.pays) ticket.pays = dbMatch.pays;
            }
          }
        } else if (mapping === 'evenement') {
          // Format event name: add match number prefix if found
          // "Brazil vs Morocco - World Cup - Group C (Match 7)" → "M7 Brazil vs Morocco - World Cup - Group C (Match 7)"
          let eventName = value.trim();
          
          // Extract match number from patterns like (Match 49), (Match M7), (Match M49)
          const matchPattern = /\(Match\s*M?(\d+)\)/i;
          const matchResult = eventName.match(matchPattern);
          
          if (matchResult) {
            const matchNum = parseInt(matchResult[1]);
            // Check if already prefixed with M + number
            if (!/^M\d+\s/i.test(eventName)) {
              eventName = `M${matchNum} ${eventName}`;
            }
          }
          
          ticket.evenement = eventName;
        } else if (mapping === 'categorie') {
          const catValue = value.trim();
          // Normalize to remove accents for matching
          const catNorm = catValue.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
          if (/^[1-9]$/.test(catValue)) {
            ticket.categorie = `CATEGORIE ${catValue}`;
          } else if (/^category\s*[1-9]$/i.test(catNorm)) {
            const num = catNorm.match(/[1-9]/)[0];
            ticket.categorie = `CATEGORIE ${num}`;
          } else if (/^cat\.?\s*[1-9]$/i.test(catNorm)) {
            const num = catNorm.match(/[1-9]/)[0];
            ticket.categorie = `CATEGORIE ${num}`;
          } else if (/^categorie\s*[1-9]$/i.test(catNorm)) {
            const num = catNorm.match(/[1-9]/)[0];
            ticket.categorie = `CATEGORIE ${num}`;
          } else if (/gold|^or$|^cat\.?\s*or$/i.test(catNorm)) {
            ticket.categorie = 'GOLD';
          } else if (/plein\s*tarif/i.test(catNorm)) {
            ticket.categorie = 'PLEIN TARIF';
          } else {
            ticket.categorie = catValue.toUpperCase();
          }
        } else if (mapping === 'session') {
          // Session Day/Night
          const sessValue = value.trim().toUpperCase();
          if (sessValue === 'DAY' || sessValue === 'JOUR' || sessValue === '1') {
            ticket.session = 'Day Session';
          } else if (sessValue === 'NIGHT' || sessValue === 'NUIT' || sessValue === 'EVENING' || sessValue === '2') {
            ticket.session = 'Evening Session';
          } else {
            ticket.session = sessValue;
          }
        } else if (mapping === 'qty') {
          // ACHAT avec sièges individuels: 1 ligne = 1 billet (qty=1), lot_size sert au groupage ref_achat
          // VENTE marketplace (1 ligne = 1 transaction): garder la VRAIE quantité
          const lotSize = parseInt(value.replace(/\s/g, '')) || 1;
          ticket._lot_size = lotSize;
          ticket.qty = importType === 'VENTE' ? lotSize : 1;
        } else if (mapping === 'prix' || mapping === 'total') {
          let cleanValue = value.replace(/\s/g, '').replace(/[€$£]/g, '').trim();
          // Détection intelligente du format numérique:
          // "1,150" = 1150 (virgule = séparateur de milliers si 3 chiffres après)
          // "1,15" = 1.15 (virgule = séparateur décimal si 1-2 chiffres après)
          // "1.150,00" = 1150.00 (format européen classique)
          // "1,150.00" = 1150.00 (format anglais)
          const hasComma = cleanValue.indexOf(',') !== -1;
          const hasDot = cleanValue.indexOf('.') !== -1;
          
          if (hasComma && hasDot) {
            // Both comma and dot: determine which is decimal
            if (cleanValue.indexOf(',') < cleanValue.indexOf('.')) {
              // "1,150.00" → comma is thousands
              cleanValue = cleanValue.replace(/,/g, '');
            } else {
              // "1.150,00" → dot is thousands, comma is decimal
              cleanValue = cleanValue.replace(/\./g, '').replace(',', '.');
            }
          } else if (hasComma && !hasDot) {
            // Only comma: check digits after comma
            const afterComma = cleanValue.split(',')[1] || '';
            if (afterComma.length === 3) {
              // "1,150" → comma is thousands separator → 1150
              cleanValue = cleanValue.replace(',', '');
            } else {
              // "1,15" → comma is decimal separator → 1.15
              cleanValue = cleanValue.replace(',', '.');
            }
          }
          // If only dot, parseFloat handles it natively
          ticket[mapping] = parseFloat(cleanValue) || 0;
        } else if (mapping === 'date_evenement' || mapping === 'date_operation') {
          let dateValue = value;
          // ISO format: 2026-01-29T18:44:10 -> 29/01/2026
          const isoMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
          if (isoMatch) {
            dateValue = `${isoMatch[3]}/${isoMatch[2]}/${isoMatch[1]}`;
          }
          // French format with day name: "ven. 29/05/26 23:00" -> "29/05/2026"
          const frenchMatch = value.match(/\d{1,2}\/\d{1,2}\/(\d{2,4})/);
          if (frenchMatch) {
            const parts = value.match(/(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
            if (parts) {
              let year = parts[3];
              if (year.length === 2) year = '20' + year;
              dateValue = `${parts[1].padStart(2, '0')}/${parts[2].padStart(2, '0')}/${year}`;
            }
          }
          // English date: "24 May", "7 June", "May 24" etc
          const engDateMatch = value.match(/^(\d{1,2})\s+(\w+)(?:\s+\d{4})?$/i);
          if (engDateMatch) {
            const months = { january:'01',february:'02',march:'03',april:'04',may:'05',june:'06',july:'07',august:'08',september:'09',october:'10',november:'11',december:'12',
              jan:'01',feb:'02',mar:'03',apr:'04',jun:'06',jul:'07',aug:'08',sep:'09',oct:'10',nov:'11',dec:'12',
              janvier:'01',fevrier:'02',mars:'03',avril:'04',mai:'05',juin:'06',juillet:'07',aout:'08',septembre:'09',octobre:'10',novembre:'11',decembre:'12' };
            const mm = months[engDateMatch[2].toLowerCase()];
            if (mm) dateValue = `${engDateMatch[1].padStart(2, '0')}/${mm}/2026`;
          }
          ticket[mapping] = dateValue;
        } else if (mapping === 'placement') {
          // Combined placement: "C9 S51 R11 S122" → split into acces, escalier, rang, seat
          const parsed = parsePlacement(value);
          if (parsed) {
            ticket.acces = parsed.acces;
            ticket.escalier = parsed.escalier;
            ticket.rang = parsed.rang;
            ticket.place = parsed.seat;
            // Déduire le court depuis le code d'accès (C = Chatrier, L = Lenglen)
            const accessPrefix = parsed.acces.charAt(0).toUpperCase();
            if (accessPrefix === 'C' && !ticket.lieu) {
              ticket.lieu = 'Court Philippe Chatrier';
            } else if (accessPrefix === 'L' && !ticket.lieu) {
              ticket.lieu = 'Court Suzanne Lenglen';
            }
          }
          // "Plein tarif" ou texte non-parseable → ignorer silencieusement
        } else if (mapping === 'lieu') {
          // Normaliser le court pour Roland Garros
          const courtValue = value.trim();
          ticket.lieu = normalizeRGCourt(courtValue) || courtValue;
        } else {
          ticket[mapping] = value;
        }
      });
      
      // Normalisation finale pour Roland Garros
      if (projectName?.toUpperCase().includes('ROLAN') || projectName?.toUpperCase().includes('RG')) {
        // Normaliser la catégorie
        if (ticket.categorie) {
          ticket.categorie = normalizeRGCategory(ticket.categorie);
        }
        
        // Normaliser le court/lieu avec rgSchedule
        if (ticket.lieu) {
          const nc = normalizeCourt(ticket.lieu);
          if (nc) ticket.lieu = nc;
          else ticket.lieu = normalizeRGCourt(ticket.lieu);
        }
        
        // Normaliser la session
        if (ticket.session) {
          const ns = normalizeSession(ticket.session);
          if (ns) ticket.session = ns;
        }
        
        // Si pas de court, déduire depuis le code d'accès (C = Chatrier, L = Lenglen)
        if (!ticket.lieu && ticket.acces) {
          const prefix = ticket.acces.charAt(0).toUpperCase();
          if (prefix === 'C') ticket.lieu = 'Court Philippe Chatrier';
          else if (prefix === 'L') ticket.lieu = 'Court Suzanne Lenglen';
        }
        
        // Détecter ANN / ANNEXE dans le nom d'événement → Outside Courts
        const phaseCheck = normalizePhase(ticket.evenement, ticket.date_evenement, projectName);
        if (phaseCheck === '__OUTSIDE__') {
          ticket.lieu = 'Outside Courts';
          // Pas d'ACCES/ESCALIER/RANG/SEAT pour Outside
          ticket.acces = '';
          ticket.escalier = '';
          ticket.rang = '';
          ticket.place = '';
        }
        
        // NORMALISER LA PHASE D'ABORD (pour détecter SEMI FINAL DAY MAN vs SEMI FINAL NIGHT MAN)
        let normalizedPhase = normalizePhase(ticket.evenement, ticket.date_evenement, projectName);
        
        // Si la phase normalisée est Men's Semifinals (générique), utiliser la session pour déterminer #1 ou #2
        if (normalizedPhase === "Men's Semifinals" && ticket.session) {
          if (ticket.session === 'Day Session') {
            normalizedPhase = "Men's Semifinal #1";
          } else if (ticket.session === 'Evening Session') {
            normalizedPhase = "Men's Semifinal #2";
          }
        }
        
        // TOUJOURS utiliser autoFillMatchName pour générer le nom correct
        const phaseRaw = normalizedPhase || ticket.evenement || null;
        const matchEntry = findMatch(
          ticket.date_evenement,
          ticket.lieu,
          ticket.session,
          ticket.tribune,
          phaseRaw,
          projectName
        );
        if (matchEntry) {
          ticket.evenement = buildMatchName(matchEntry);
          // Remplir session et lieu depuis l'entrée du calendrier si vides
          if (!ticket.session && matchEntry.session) {
            ticket.session = matchEntry.session;
          }
          if (!ticket.lieu && matchEntry.court) {
            ticket.lieu = matchEntry.court;
          }
        } else {
          const autoName = autoFillMatchName(
            ticket.date_evenement,
            ticket.lieu,
            ticket.session,
            ticket.tribune,
            phaseRaw,
            projectName
          );
          if (autoName) {
            ticket.evenement = autoName;
          } else if (ticket.evenement) {
            ticket.evenement = ticket.evenement.replace(/\(RG\)/gi, '').trim();
          }
        }
      }
      
      // Calculate total if needed
      if (!ticket.total && ticket.prix && ticket.qty) {
        ticket.total = ticket.prix * ticket.qty;
      }
      // Prix unitaire déduit du total si absent (ex: CSV marketplace avec Total seul)
      if (!ticket.prix && ticket.total && ticket.qty > 0) {
        ticket.prix = Math.round((ticket.total / ticket.qty) * 100) / 100;
      }
      
      // ============ Normalisation WORLD CUP 2026 ============
      if (projectName?.toUpperCase().startsWith('WORLD CUP')) {
        // Détecter le numéro de match depuis différents champs
        let matchNum = null;
        
        // Chercher dans les champs possibles
        const fieldsToCheck = [ticket.evenement, ticket.match_no, ticket.ref];
        for (const field of fieldsToCheck) {
          if (field) {
            matchNum = normalizeWCMatchNumber(field);
            if (matchNum) break;
          }
        }
        
        // Si on a trouvé un numéro de match, auto-remplir les infos
        // PRIORITÉ 1: Données existantes en base (matchDetailsCache)
        // PRIORITÉ 2: Données statiques WC_MATCHES (fallback)
        if (matchNum) {
          const dbMatch = matchDetailsCache[matchNum];
          if (dbMatch && dbMatch.evenement) {
            // Utiliser les données de la base de données
            ticket.evenement = dbMatch.evenement;
            if (!ticket.date_evenement && dbMatch.date_evenement) ticket.date_evenement = dbMatch.date_evenement;
            if (!ticket.lieu && dbMatch.lieu) ticket.lieu = dbMatch.lieu;
            if (!ticket.ville && dbMatch.ville) ticket.ville = dbMatch.ville;
            if (!ticket.pays && dbMatch.pays) ticket.pays = dbMatch.pays;
            ticket.match_num = matchNum;
          } else {
            // Fallback: données statiques WC_MATCHES
            const match = getWCMatchByNumber(matchNum);
            if (match) {
              ticket.evenement = getWCMatchDisplayName(matchNum);
              if (!ticket.date_evenement) ticket.date_evenement = match.date;
              if (!ticket.lieu) ticket.lieu = match.venue;
              if (!ticket.ville) ticket.ville = match.city;
              if (!ticket.phase) ticket.phase = match.stage;
              ticket.match_num = matchNum;
            }
          }
        }
        
        // Normaliser le stade si fourni
        if (ticket.lieu) {
          ticket.lieu = normalizeWCVenue(ticket.lieu);
        }
        
        // Normaliser la phase/stage
        if (ticket.phase) {
          ticket.phase = normalizeWCStage(ticket.phase);
        }
      }
      
      // ============ Normalisation RUGBY WORLD CUP 2027 ============
      if (projectName?.toUpperCase().includes('RUGBY')) {
        let rugbyMatch = null;
        const fieldsToCheckRugby = [ticket.evenement, ticket.match_no, ticket.ref];
        for (const field of fieldsToCheckRugby) {
          if (field) {
            const num = normalizeRugbyMatchNumber(field);
            if (num) { rugbyMatch = getRugbyMatchByNumber(num); break; }
            rugbyMatch = detectRugbyMatchFromText(field, ticket.date_evenement, ticket.ville || ticket.lieu);
            if (rugbyMatch) break;
          }
        }
        if (rugbyMatch) {
          // Nom STANDARD : toujours écrit pareil
          ticket.evenement = getRugbyMatchDisplayName(rugbyMatch.matchNum);
          if (!ticket.date_evenement) ticket.date_evenement = rugbyMatch.date;
          if (!ticket.heure_evenement) ticket.heure_evenement = rugbyMatch.time;
          if (!ticket.lieu) ticket.lieu = rugbyMatch.venue;
          if (!ticket.ville) ticket.ville = rugbyMatch.city;
          if (!ticket.pays) ticket.pays = 'Australie';
          if (!ticket.phase) ticket.phase = rugbyMatch.stage;
          ticket.match_num = rugbyMatch.matchNum;
        }
      }
      
      // Set defaults for missing required fields
      if (importType === 'ACHAT') {
        if (!ticket.evenement) ticket.evenement = 'ATTENTE D\'INFO';
        if (!ticket.categorie) ticket.categorie = 'ATTENTE D\'INFO';
        if (!ticket.source) ticket.source = 'ATTENTE D\'INFO';
        // Auto-remplir "Date achat" avec la date du jour si pas de date_operation
        if (!ticket.date_operation) {
          const today = new Date();
          const dd = String(today.getDate()).padStart(2, '0');
          const mm = String(today.getMonth() + 1).padStart(2, '0');
          const yyyy = today.getFullYear();
          ticket.date_operation = `${dd}/${mm}/${yyyy}`;
        }
      }
      
      // Only add if has meaningful data - check all mapped fields
      const hasMeaningfulData = Object.entries(columnMappings).some(([idx, mapping]) => {
        if (!mapping || mapping === 'ignore') return false;
        const value = row[parseInt(idx)] || '';
        return value.toString().trim().length > 0;
      });
      
      if (hasMeaningfulData) {
        tickets.push(ticket);
      }
    }
    
    return tickets;
  };

  // Auto-generate ref_achat for tickets without one (RG imports only, NOT World Cup)
  const autoGenerateRefs = async (tickets) => {
    // Skip ref generation for World Cup projects
    const isWC = projectName && projectName.toUpperCase().startsWith('WORLD CUP');
    if (isWC) return tickets;

    // Find tickets without ref_achat
    const needsRef = tickets.filter(t => !t.ref_achat && t.type === 'ACHAT');
    if (needsRef.length === 0) return tickets;
    
    // Group tickets by source + lot_size + event + category (same lot = same ref)
    // _lot_size comes from "# tickets" column and indicates how many tickets belong together
    const lots = [];
    let currentLot = null;
    
    for (const t of needsRef) {
      const source = (t.source || 'UNKNOWN').toUpperCase().trim();
      const lotSize = parseInt(t._lot_size) || 1;
      const event = (t.evenement || '').trim();
      const cat = (t.categorie || '').trim();
      
      // Same lot if: same source + same lot_size + same event + same category AND lot not full
      if (currentLot && currentLot.source === source && currentLot.lotSize === lotSize && 
          currentLot.event === event && currentLot.cat === cat && 
          currentLot.tickets.length < lotSize) {
        currentLot.tickets.push(t);
      } else {
        // Start new lot
        currentLot = { source, lotSize, event, cat, tickets: [t] };
        lots.push(currentLot);
      }
    }
    
    // For each unique source, get the last ref number
    const sources = [...new Set(lots.map(l => l.source))];
    const lastRefs = {};
    
    for (const src of sources) {
      try {
        const data = await ticketService.getLastRef(src, projectName);
        lastRefs[src] = { prefix: data.prefix, lastNum: data.last_number };
      } catch (e) {
        console.error(`Failed to get last ref for ${src}:`, e);
        lastRefs[src] = { prefix: src.substring(0, 4).toUpperCase(), lastNum: 0 };
      }
    }
    
    // Assign refs to lots
    for (const lot of lots) {
      const refInfo = lastRefs[lot.source];
      if (!refInfo) continue;
      
      refInfo.lastNum += 1;
      const ref = `${refInfo.prefix}-${String(refInfo.lastNum).padStart(2, '0')}`;
      
      for (const t of lot.tickets) {
        t.ref_achat = ref;
      }
    }
    
    // Clean up _lot_size before returning (internal field only)
    for (const t of tickets) {
      delete t._lot_size;
    }
    
    return tickets;
  };

  // Check for duplicates before going to step 3
  const handleGoToStep3 = async () => {
    setCheckingDuplicates(true);
    setDuplicateCheckResult(null);
    try {
      let tickets = buildTickets();
      if (tickets.length === 0) {
        setStep(3);
        setCheckingDuplicates(false);
        return;
      }
      
      // Auto-generate ref_achat for tickets that don't have one
      tickets = await autoGenerateRefs(tickets);
      
      const result = await ticketService.checkDuplicates(tickets, projectName);
      setDuplicateCheckResult(result);
    } catch (error) {
      console.error('Duplicate check failed:', error);
      setDuplicateCheckResult(null);
    }
    setStep(3);
    setCheckingDuplicates(false);
  };

  // Handle import
  const handleImport = async () => {
    setLoading(true);
    setResult(null);
    
    try {
      let tickets = buildTickets();
      
      if (tickets.length === 0) {
        setResult({ success: false, message: 'Aucun billet valide trouvé' });
        setLoading(false);
        return;
      }
      
      // Auto-generate ref_achat for tickets that don't have one
      tickets = await autoGenerateRefs(tickets);
      
      const res = await onImport(tickets);
      
      const created = res.created || 0;
      const updated = res.updated || 0;
      const skipped = res.skipped || 0;
      
      let message = `✅ Import terminé: ${created} créé(s)`;
      if (updated > 0) message += `, ${updated} mis à jour`;
      if (skipped > 0) message += `, ${skipped} ignoré(s)`;
      
      toast.success(message, { duration: 4000 });
      
      onOpenChange(false);
    } catch (error) {
      setResult({ success: false, message: error.message || 'Erreur lors de l\'import' });
      toast.error('Erreur lors de l\'import');
    }
    setLoading(false);
  };

  // Count mapped vs total columns
  const mappedCount = Object.values(columnMappings).filter(v => v && v !== 'ignore').length;
  const totalColumns = parsedData.columns.length;

  // Get fields specific to this project AND import type (ACHAT/VENTE)
  const projectFields = getFieldsForProject(projectName, importType);
  
  // Filtered fields for dropdown
  const filteredFields = searchField 
    ? projectFields.filter(f => f.label.toLowerCase().includes(searchField.toLowerCase()))
    : projectFields;
  
  // Get preview columns for this project AND import type
  const previewColumns = getPreviewColumnsForProject(projectName, importType);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden bg-zinc-900 border-zinc-700">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl text-white">
            <Zap className="h-5 w-5 text-[#C4A052]" />
            Import Intelligent CSV
          </DialogTitle>
        </DialogHeader>

        {/* Steps indicator */}
        <div className="flex items-center justify-center gap-2 py-3">
          <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${step === 1 ? 'bg-[#C4A052] text-black' : 'bg-zinc-800 text-zinc-400'}`}>
            <Upload className="h-4 w-4" />
            1. Fichier
          </div>
          <ArrowRight className="h-4 w-4 text-zinc-500" />
          <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${step === 2 ? 'bg-[#C4A052] text-black' : 'bg-zinc-800 text-zinc-400'}`}>
            <FileSpreadsheet className="h-4 w-4" />
            2. Mapping
          </div>
          <ArrowRight className="h-4 w-4 text-zinc-500" />
          <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${step === 3 ? 'bg-emerald-500 text-white' : 'bg-zinc-800 text-zinc-400'}`}>
            <Check className="h-4 w-4" />
            3. Import
          </div>
        </div>

        <div className="overflow-y-auto max-h-[60vh] p-4">
          {/* Step 1: Paste/Upload */}
          {step === 1 && (
            <div className="space-y-4">
              {/* Type selection */}
              <div className="flex items-center gap-4 justify-center">
                <span className="text-zinc-400">Type:</span>
                <Button
                  variant={importType === 'ACHAT' ? 'default' : 'outline'}
                  onClick={() => setImportType('ACHAT')}
                  className={importType === 'ACHAT' ? 'bg-[#C4A052] text-black' : 'border-zinc-600'}
                >
                  <ShoppingCart className="h-4 w-4 mr-2" />
                  ACHAT
                </Button>
                <Button
                  variant={importType === 'VENTE' ? 'default' : 'outline'}
                  onClick={() => setImportType('VENTE')}
                  className={importType === 'VENTE' ? 'bg-emerald-500 text-white' : 'border-zinc-600'}
                >
                  <TrendingUp className="h-4 w-4 mr-2" />
                  VENTE
                </Button>
              </div>

              {/* Header checkbox + Help message */}
              <div className="bg-zinc-800 border border-zinc-700 rounded-lg p-3 space-y-3">
                <label className="flex items-center gap-3 cursor-pointer select-none" data-testid="has-header-checkbox">
                  <input
                    type="checkbox"
                    checked={hasHeader}
                    onChange={(e) => {
                      setHasHeader(e.target.checked);
                      if (rawData.trim()) {
                        handleDataChange(rawData, e.target.checked);
                      }
                    }}
                    className="w-4 h-4 rounded border-zinc-600 bg-zinc-700 text-[#C4A052] focus:ring-[#C4A052] accent-[#C4A052]"
                  />
                  <span className="text-white font-medium text-sm">
                    Première ligne = en-têtes de colonnes
                  </span>
                </label>
                <p className="text-xs text-zinc-400 ml-7">
                  {hasHeader 
                    ? 'Les colonnes seront détectées automatiquement depuis les noms en première ligne (DATE, CATEGORY, Prix, etc.)'
                    : 'Vous choisirez manuellement chaque colonne à l\'étape suivante'
                  }
                </p>
              </div>

              <Textarea
                value={rawData}
                onChange={(e) => handleDataChange(e.target.value)}
                placeholder="Collez vos données AVEC la ligne d'en-tête...&#10;&#10;Exemple:&#10;DATE	GAME	CATEGORY	Tribune	ACCES	ESCALIER	ROW	SEAT	Prix	SOURCE	REF ACHAT&#10;24/05		CAT OR	Borotra	C29	16	8	128	350€	DEAL 2	JUST-01"
                className="min-h-[280px] bg-zinc-800 border-zinc-700 text-white font-mono text-sm"
              />

              {parsedData.rows.length > 0 && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-emerald-400">
                    ✅ {parsedData.columns.length} colonnes détectées | {parsedData.rows.length} lignes de données
                  </span>
                  <Button
                    onClick={handleGoToStep2}
                    className="bg-[#C4A052] hover:bg-[#D4B86A] text-black"
                    data-testid="continue-to-mapping-btn"
                  >
                    Continuer {hasHeader ? '→ Mapping Auto' : '→ Mapping Manuel'}
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Step 2: Column Mapping */}
          {step === 2 && (
            <div className="space-y-4">
              {/* Auto-detection Summary */}
              <div className="flex items-center justify-between text-sm bg-zinc-800 p-3 rounded-lg">
                <div className="flex gap-4 items-center">
                  <span className="flex items-center gap-1.5 text-emerald-400 font-medium">
                    <CheckCircle className="h-4 w-4" />
                    Détection automatique
                  </span>
                  <span className="text-zinc-400">
                    📊 {totalColumns} colonnes
                  </span>
                  <span className="text-emerald-400">
                    ✅ {mappedCount} mappées
                  </span>
                  {totalColumns - mappedCount > 0 && (
                    <span className="text-amber-400">
                      ⚠️ {totalColumns - mappedCount} à vérifier
                    </span>
                  )}
                </div>
                <span className="text-zinc-400">{parsedData.rows.length} lignes de données</span>
              </div>

              {/* Column mapping table */}
              <div className="border border-zinc-700 rounded-lg overflow-hidden">
                <div className="grid grid-cols-3 gap-4 p-3 bg-zinc-800 border-b border-zinc-700 font-medium text-sm">
                  <div className="text-zinc-400">Colonne CSV</div>
                  <div className="text-zinc-400 flex items-center gap-2">
                    <ArrowRight className="h-4 w-4" />
                    Champ YAS
                  </div>
                  <div className="text-zinc-400">Aperçu</div>
                </div>

                <div className="max-h-[350px] overflow-y-auto">
                  {parsedData.columns.map((col, idx) => (
                    <div key={idx} className="grid grid-cols-3 gap-4 p-3 border-b border-zinc-800 items-center hover:bg-zinc-800/50">
                      {/* Column header/sample */}
                      <div className="flex items-center gap-2">
                        <span className="bg-zinc-700 px-2 py-1 rounded text-sm text-white truncate max-w-[150px]" title={col.header}>
                          {col.header}
                        </span>
                      </div>

                      {/* Mapping dropdown */}
                      <div className="relative">
                        <button
                          onClick={() => setActiveDropdown(activeDropdown === idx ? null : idx)}
                          className={`w-full flex items-center justify-between px-3 py-2 rounded border ${
                            columnMappings[idx] === 'ignore' 
                              ? 'bg-red-900/30 border-red-500/50 text-red-400'
                              : columnMappings[idx]
                                ? 'bg-emerald-900/30 border-emerald-500/50 text-emerald-400'
                                : 'bg-zinc-800 border-zinc-600 text-zinc-400'
                          }`}
                        >
                          <span className="truncate">
                            {YAS_FIELDS.find(f => f.key === columnMappings[idx])?.label || 'Sélectionner...'}
                          </span>
                          <ChevronDown className="h-4 w-4 flex-shrink-0" />
                        </button>

                        {/* Dropdown */}
                        {activeDropdown === idx && (
                          <div className="absolute z-50 w-64 mt-1 bg-zinc-800 border border-zinc-600 rounded-lg shadow-xl max-h-[250px] overflow-y-auto">
                            {/* Search */}
                            <div className="p-2 border-b border-zinc-700 sticky top-0 bg-zinc-800">
                              <div className="relative">
                                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
                                <Input
                                  value={searchField}
                                  onChange={(e) => setSearchField(e.target.value)}
                                  placeholder="Rechercher un champ..."
                                  className="pl-8 h-8 bg-zinc-700 border-zinc-600 text-white text-sm"
                                  autoFocus
                                />
                              </div>
                            </div>
                            
                            {/* Options */}
                            <div className="py-1">
                              {filteredFields.map(field => (
                                <button
                                  key={field.key}
                                  onClick={() => {
                                    updateMapping(idx, field.key);
                                    setSearchField('');
                                  }}
                                  className={`w-full px-3 py-2 text-left text-sm hover:bg-zinc-700 flex items-center justify-between ${
                                    columnMappings[idx] === field.key ? 'bg-zinc-700' : ''
                                  } ${field.key === 'ignore' ? 'text-red-400' : 'text-white'}`}
                                >
                                  <span>{field.label}</span>
                                  {columnMappings[idx] === field.key && (
                                    <Check className="h-4 w-4 text-emerald-400" />
                                  )}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Preview */}
                      <div className="text-sm text-zinc-400 truncate" title={col.samples.join(', ')}>
                        {col.samples[0] || '-'}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Actions - Step 2 */}
              <div className="flex items-center justify-between pt-4 border-t border-zinc-700">
                <Button
                  variant="outline"
                  onClick={() => setStep(1)}
                  className="border-zinc-600"
                >
                  ← Retour
                </Button>
                
                <div className="flex items-center gap-2">
                  {/* Saved mappings indicator */}
                  {matchedSavedMapping && (
                    <span className="text-xs text-emerald-400 px-2 py-1 bg-emerald-900/30 rounded">
                      ✓ Mapping "{matchedSavedMapping.name}"
                    </span>
                  )}
                  
                  {/* Project default mapping indicator */}
                  {projectDefaultApplied && !matchedSavedMapping && (
                    <span className="text-xs text-[#C4A052] px-2 py-1 bg-[#C4A052]/20 rounded flex items-center gap-1">
                      🎾 {projectName}
                    </span>
                  )}
                  
                  {/* Save mapping button */}
                  {!showSaveDialog ? (
                    <Button
                      variant="outline"
                      onClick={() => setShowSaveDialog(true)}
                      className="border-zinc-600 text-zinc-300"
                      size="sm"
                    >
                      💾 Sauvegarder
                    </Button>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Input
                        value={savedMappingName}
                        onChange={(e) => setSavedMappingName(e.target.value)}
                        placeholder="Nom du mapping..."
                        className="h-8 w-40 bg-zinc-700 border-zinc-600 text-white text-sm"
                        autoFocus
                      />
                      <Button
                        size="sm"
                        onClick={async () => {
                          if (savedMappingName.trim()) {
                            const saved = await saveMappingToDB(savedMappingName.trim(), parsedData.headers, columnMappings, projectName);
                            if (saved) {
                              toast.success(`Mapping "${savedMappingName}" sauvegardé dans la base de données !`);
                              setShowSaveDialog(false);
                              setSavedMappingName('');
                              await refreshMappings(); // Refresh the list
                            } else {
                              toast.error('Erreur lors de la sauvegarde');
                            }
                          }
                        }}
                        disabled={!savedMappingName.trim()}
                        className="bg-emerald-600 hover:bg-emerald-700"
                      >
                        ✓
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setShowSaveDialog(false);
                          setSavedMappingName('');
                        }}
                      >
                        ✕
                      </Button>
                    </div>
                  )}
                  
                  {/* View saved mappings */}
                  <Button
                    variant="outline"
                    onClick={() => setShowSavedMappings(!showSavedMappings)}
                    className="border-zinc-600 text-zinc-300"
                    size="sm"
                  >
                    📋 Mes mappings
                  </Button>
                </div>
                
                <div className="flex items-center gap-2">
                  {/* Preview button */}
                  <Button
                    variant="outline"
                    onClick={() => setShowDataPreview(!showDataPreview)}
                    className={showDataPreview 
                      ? "border-emerald-500 text-emerald-400 bg-emerald-900/20" 
                      : "border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10"
                    }
                    size="sm"
                    disabled={mappedCount === 0}
                  >
                    👁️ {showDataPreview ? 'Masquer aperçu' : 'Voir aperçu'}
                  </Button>
                  
                  <Button
                    onClick={handleGoToStep3}
                    disabled={mappedCount === 0 || checkingDuplicates}
                    className="bg-[#C4A052] hover:bg-[#D4B86A] text-black"
                  >
                    {checkingDuplicates ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Vérification...
                      </>
                    ) : (
                      'Continuer → Import'
                    )}
                  </Button>
                </div>
              </div>
              
              {/* Saved mappings panel */}
              {showSavedMappings && (
                <div className="mt-4 p-3 bg-zinc-800/50 border border-zinc-700 rounded-lg">
                  <h4 className="text-sm font-medium text-white mb-2">Mappings sauvegardés</h4>
                  {loadingMappings ? (
                    <p className="text-xs text-zinc-500">Chargement...</p>
                  ) : dbMappings.length === 0 ? (
                    <p className="text-xs text-zinc-500">Aucun mapping sauvegardé</p>
                  ) : (
                    <div className="space-y-2 max-h-32 overflow-y-auto">
                      {dbMappings.map((m) => (
                        <div key={m.id} className="flex items-center justify-between text-xs bg-zinc-700/50 p-2 rounded">
                          <div>
                            <span className="text-white font-medium">{m.name}</span>
                            <span className="text-zinc-500 ml-2">
                              {new Date(m.createdAt).toLocaleDateString('fr-FR')}
                            </span>
                            {m.project && (
                              <span className="text-amber-400 ml-2 text-[10px]">({m.project})</span>
                            )}
                          </div>
                          <div className="flex gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-6 px-2 text-emerald-400 hover:text-emerald-300"
                              onClick={() => {
                                setColumnMappings(m.mappings);
                                setMatchedSavedMapping(m);
                                setShowSavedMappings(false);
                                setShowDataPreview(true); // Afficher l'aperçu automatiquement
                                toast.success(`Mapping "${m.name}" appliqué - Vérifiez l'aperçu ci-dessous`);
                              }}
                            >
                              Appliquer
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-6 px-2 text-red-400 hover:text-red-300"
                              onClick={async () => {
                                if (await deleteMappingFromDB(m.id)) {
                                  toast.success('Mapping supprimé');
                                  await refreshMappings();
                                } else {
                                  toast.error('Erreur lors de la suppression');
                                }
                              }}
                            >
                              ✕
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Data Preview Panel - Shown after applying a saved mapping */}
              {showDataPreview && (
                <div className="mt-4 p-4 bg-gradient-to-br from-emerald-900/30 to-zinc-800/50 border border-emerald-500/30 rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-medium text-emerald-400 flex items-center gap-2">
                      👁️ Aperçu des données ({buildTickets().length} tickets)
                    </h4>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setShowDataPreview(false)}
                      className="h-6 px-2 text-zinc-400 hover:text-white"
                    >
                      ✕ Fermer
                    </Button>
                  </div>
                  
                  {/* Preview table - COLONNES DYNAMIQUES PAR PROJET - TOUTES LES LIGNES */}
                  <div className="overflow-x-auto max-h-[500px] overflow-y-auto border border-zinc-700 rounded">
                    <table className="w-full text-xs min-w-[900px]">
                      <thead className="bg-zinc-800 sticky top-0 z-10">
                        <tr>
                          <th className="px-2 py-1.5 text-left text-zinc-400 font-medium whitespace-nowrap">#</th>
                          {previewColumns.map(col => (
                            <th key={col.key} className="px-2 py-1.5 text-left text-zinc-400 font-medium whitespace-nowrap">
                              {col.label}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {buildTickets().map((ticket, idx) => (
                          <tr key={idx} className="border-b border-zinc-700/50 hover:bg-zinc-700/30">
                            <td className="px-2 py-1.5 text-zinc-500">{idx + 1}</td>
                            {previewColumns.map(col => (
                              <td key={col.key} className="px-2 py-1.5 text-zinc-300 whitespace-nowrap max-w-[150px] truncate" title={ticket[col.key] || ''}>
                                {ticket[col.key] || <span className="text-zinc-600">-</span>}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  {/* Quick stats */}
                  <div className="flex gap-4 mt-3 pt-3 border-t border-zinc-700">
                    <span className="text-xs text-zinc-400">
                      📊 <span className="text-white">{buildTickets().length}</span> tickets à importer
                    </span>
                    <span className="text-xs text-zinc-400">
                      ✅ <span className="text-emerald-400">{mappedCount}</span> colonnes mappées
                    </span>
                    <span className={`text-xs ${importType === 'ACHAT' ? 'text-[#C4A052]' : 'text-emerald-400'}`}>
                      Type: {importType}
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 3: Confirm and Import */}
          {step === 3 && (
            <div className="space-y-4">
              {/* Project Mismatch Warnings */}
              {duplicateCheckResult?.project_warnings?.length > 0 && (
                <div className="bg-red-500/10 border border-red-500/40 p-4 rounded-lg" data-testid="project-warnings-panel">
                  <h3 className="font-medium text-red-400 flex items-center gap-2 mb-2">
                    <AlertCircle className="h-4 w-4" />
                    Attention : incohérence de projet ({duplicateCheckResult.project_warnings.length})
                  </h3>
                  <p className="text-xs text-red-300/80 mb-2">
                    Ces billets ne semblent pas correspondre au projet "{projectName}". Vérifiez avant d'importer.
                  </p>
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {duplicateCheckResult.project_warnings.map((w, idx) => (
                      <div key={idx} className="text-xs bg-red-900/20 p-2 rounded flex items-center gap-2">
                        <span className="text-red-400 font-mono">{w.ref}</span>
                        <span className="text-zinc-400">→</span>
                        <span className="text-white">{w.event}</span>
                        <span className="text-red-300 text-[10px] ml-auto">{w.reason}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Duplicate Detection Results */}
              {duplicateCheckResult && duplicateCheckResult.update_count > 0 && (
                <div className="bg-amber-500/10 border border-amber-500/40 p-4 rounded-lg" data-testid="duplicates-panel">
                  <h3 className="font-medium text-amber-400 flex items-center gap-2 mb-2">
                    <AlertCircle className="h-4 w-4" />
                    {duplicateCheckResult.update_count} doublon(s) détecté(s) — seront mis à jour
                  </h3>
                  <p className="text-xs text-amber-300/80 mb-2">
                    Ces références existent déjà en base. Les champs non vides seront mis à jour sans écraser les données existantes.
                  </p>
                  <div className="space-y-1 max-h-40 overflow-y-auto">
                    {duplicateCheckResult.duplicates.map((d, idx) => (
                      <div key={idx} className="text-xs bg-amber-900/20 p-2 rounded flex items-center gap-2">
                        <span className={`font-semibold px-1.5 py-0.5 rounded ${d.type === 'ACHAT' ? 'bg-[#C4A052]/20 text-[#C4A052]' : 'bg-emerald-500/20 text-emerald-400'}`}>
                          {d.type}
                        </span>
                        <span className="text-amber-400 font-mono">{d.ref}</span>
                        <span className="text-zinc-500">|</span>
                        <span className="text-zinc-300 truncate max-w-[200px]">{d.existing_event}</span>
                        {d.existing_categorie && (
                          <span className="text-zinc-500 text-[10px]">{d.existing_categorie}</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Summary */}
              <div className="bg-zinc-800 p-4 rounded-lg space-y-2">
                <h3 className="font-medium text-white">Résumé de l'import</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Type:</span>
                    <span className={importType === 'ACHAT' ? 'text-[#C4A052]' : 'text-emerald-400'}>
                      {importType}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Lignes:</span>
                    <span className="text-white">{parsedData.rows.length}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Colonnes mappées:</span>
                    <span className="text-emerald-400">{mappedCount}/{totalColumns}</span>
                  </div>
                  {duplicateCheckResult ? (
                    <>
                      <div className="flex justify-between">
                        <span className="text-zinc-400">Nouveaux billets:</span>
                        <span className="text-emerald-400">{duplicateCheckResult.new_count}</span>
                      </div>
                      {duplicateCheckResult.update_count > 0 && (
                        <div className="flex justify-between">
                          <span className="text-zinc-400">Mises à jour:</span>
                          <span className="text-amber-400">{duplicateCheckResult.update_count}</span>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="flex justify-between">
                      <span className="text-zinc-400">Tickets à créer:</span>
                      <span className="text-white">{buildTickets().length}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Preview first tickets */}
              <div className="bg-zinc-800 p-4 rounded-lg space-y-2">
                <h3 className="font-medium text-white">Aperçu (3 premiers)</h3>
                <div className="space-y-2 text-sm font-mono overflow-x-auto">
                  {buildTickets().slice(0, 3).map((ticket, idx) => (
                    <div key={idx} className="bg-zinc-700 p-2 rounded text-xs">
                      <span className="text-emerald-400">{ticket.evenement || 'N/A'}</span>
                      {' | '}
                      <span className="text-[#C4A052]">{ticket.categorie || 'N/A'}</span>
                      {' | '}
                      <span className="text-zinc-400">x{ticket.qty}</span>
                      {ticket.date_evenement && (
                        <>
                          {' | '}
                          <span className="text-cyan-400">📅 {ticket.date_evenement}</span>
                        </>
                      )}
                      {ticket.date_operation && (
                        <>
                          {' | '}
                          <span className="text-pink-400">🛒 {ticket.date_operation}</span>
                        </>
                      )}
                      {ticket.email_compte && (
                        <>
                          {' | '}
                          <span className="text-blue-400">{ticket.email_compte}</span>
                        </>
                      )}
                      {ticket.source && (
                        <>
                          {' | '}
                          <span className="text-purple-400">{ticket.source}</span>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Result */}
              {result && (
                <div className={`p-3 rounded-lg flex items-center gap-2 ${
                  result.success ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'
                }`}>
                  {result.success ? <CheckCircle className="h-5 w-5" /> : <AlertCircle className="h-5 w-5" />}
                  {result.message}
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-between pt-4">
                <Button
                  variant="outline"
                  onClick={() => setStep(2)}
                  className="border-zinc-600"
                  disabled={loading}
                >
                  ← Retour
                </Button>
                <Button
                  onClick={handleImport}
                  disabled={loading || buildTickets().length === 0}
                  className="bg-emerald-600 hover:bg-emerald-500 text-white min-w-[200px]"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Import en cours...
                    </>
                  ) : (
                    <>
                      <Check className="h-4 w-4 mr-2" />
                      Importer {buildTickets().length} billet(s)
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SmartImportModal;
